import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import closeIcon from '../../../../assets/accountSetting-assets/x-circle.svg';
import { classes } from './utils';
import {
  ADD_ORGANIZATIONS,
  ERROR,
  INVALID_INPUT_MESSAGES,
  INVALID_INPUT_REGEX,
  UTILS,
} from '../../../../constants/staticContent';
import { inviteOrganizationRootAdmin } from '../../../../services/addOrganizationApi';
import ExpiryTimeDropdown from '../../utils/ExpiryTimeDropdown';
import { getLocalStorageItem } from '../../../../services/localStorageUtils';
import ROLES from '../../../../constants/roles';
import { ReactComponent as CopyIcon } from '../../../../assets/common-assets/copyIcon.svg';
import { darkPalette } from '../../../../theme';

const AddOrganization = ({ setOpen }) => {
  const [invitationEmail, setInvitationEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [emailError, setEmailError] = useState('');
  const userDetail = useSelector((state) => state.user.user);
  const [organizationError, setOrganizationError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedExpiration, setSelectedExpiration] = useState({
    title: '2 Days',
    time: '2d',
  });
  const [referredBy, setReferredBy] = useState('');
  const [copied, setCopied] = useState(false);
  const loginRole = getLocalStorageItem('role');
  const handleExpireTimeValue = (newValue) => {
    setSelectedExpiration(newValue);
  };

  const handleClose = () => {
    setInvitationEmail('');
    setEmailError('');
    setOrganization('');
    setOrganizationError('');
    setOpen(false);
    setSelectedExpiration({
      title: '2 Days',
      time: '2d',
    });
    setCopied(false);
  };
  useEffect(() => {
    if (userDetail) {
      setReferredBy(userDetail.referral_code);
    }
  }, [userDetail]);
  const getEmailError = (email) => {
    const emailRegex = INVALID_INPUT_REGEX.EMAIL;
    const isFirstLatterCapital = INVALID_INPUT_REGEX.EMAIL_CAPITAL.test(email);

    if (email === '') {
      return ERROR.EMPTY_ERROR;
    }
    if (!emailRegex.test(email) || !isFirstLatterCapital) {
      return INVALID_INPUT_MESSAGES.EMAIL;
    }

    return '';
  };
  const getOrganizationError = (organizationName) => {
    if (organizationName === '') {
      return ERROR.EMPTY_ERROR;
    }
    return '';
  };
  const inviteOrganizationHandler = async () => {
    const emailValidation = getEmailError(invitationEmail);
    const organizationValidation = getOrganizationError(organization);

    if (emailValidation) {
      setEmailError(emailValidation);
    }
    if (organizationValidation) {
      setOrganizationError(organizationValidation);
    }

    if (!emailValidation && !organizationValidation) {
      setIsLoading(true);
      const expiryTime = selectedExpiration?.time;
      const response = await inviteOrganizationRootAdmin({
        invitationEmail,
        expiryTime,
        organization,
        referredBy,
      });
      setIsLoading(false);
      if (response) {
        handleClose();
      }
    }
  };
  const handleCopy = () => {
    navigator.clipboard.writeText(referredBy);
    setCopied(true);
  };
  return (
    <Card className={classes.cardComponent}>
      <CardHeader
        className={classes.cardHeader}
        title={
          <Grid container alignItems="center">
            <Grid item xs={10}>
              <Typography variant="h6">
                {ADD_ORGANIZATIONS.CARD_TITLE}
              </Typography>
            </Grid>
            <Grid item xs={2} align="right">
              <Avatar
                onClick={handleClose}
                src={closeIcon}
                className={classes.closeAction}
              />
            </Grid>
          </Grid>
        }
      />
      <CardContent className={classes.cardContent}>
        <Typography variant="subtitle1" className={classes.subTitle}>
          {ADD_ORGANIZATIONS.SUB_TITLE}
        </Typography>
        {(loginRole === ROLES.SALES_ADMIN || loginRole === ROLES.SALES) && (
          <>
            <Typography variant="subtitle1" className="inputHeading">
              {ADD_ORGANIZATIONS.REFERRAL_CODE}
            </Typography>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                fullWidth
                size="small"
                id="outlined-basic"
                variant="filled"
                className="textField"
                value={referredBy}
                disabled
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                        <IconButton onClick={handleCopy} edge="end">
                          {copied ? (
                            <CheckCircleIcon
                              sx={{ height: '24px', width: '24px' }}
                              style={{ color: darkPalette.primary.main }}
                            />
                          ) : (
                            <CopyIcon sx={{ height: '24px', width: '24px' }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </>
        )}

        <Typography variant="subtitle1" className={classes.inputHeading}>
          {ADD_ORGANIZATIONS.EMAIL}
        </Typography>

        <TextField
          fullWidth
          size="small"
          id="outlined-basic"
          variant="outlined"
          className={classes.textField}
          value={invitationEmail}
          error={Boolean(emailError)}
          helperText={emailError}
          onChange={(event) => {
            const email = event.target.value;
            setInvitationEmail(email);
            setEmailError(getEmailError(email));
          }}
        />
        <Typography variant="subtitle1" className={classes.inputHeading}>
          {ADD_ORGANIZATIONS.ORGANIZATION}
        </Typography>
        <TextField
          fullWidth
          size="small"
          id="outlined-basic"
          variant="outlined"
          className={classes.textField}
          value={organization}
          error={Boolean(organizationError)}
          helperText={organizationError}
          onChange={(event) => {
            const { value } = event.target;
            setOrganization(value);
            setOrganizationError(getOrganizationError(value));
          }}
        />

        <Typography variant="subtitle1" className={classes.inputHeading}>
          {UTILS.EXPIRY_TIME}
        </Typography>

        <ExpiryTimeDropdown
          selectedExpiration={selectedExpiration}
          handleExpireTimeValue={handleExpireTimeValue}
        />

        <LoadingButton
          variant="contained"
          alignItems="center"
          className={classes.addUserBtn}
          onClick={inviteOrganizationHandler}
          loading={isLoading}
        >
          {ADD_ORGANIZATIONS.INVITE_BUTTON}
        </LoadingButton>
      </CardContent>
    </Card>
  );
};

AddOrganization.propTypes = {
  setOpen: PropTypes.func.isRequired,
};

export default AddOrganization;
